/**
 * PrincipalFilterSelect — Single-Select Dropdown für Nutzer ODER Gruppe.
 *
 * sl-97oo: Audit-Trail-Filter. Ersetzt die reine User-Liste und erlaubt
 * zusätzlich das Filtern nach Gruppe (alle Events der Gruppen-Mitglieder).
 *
 * Re-uses the shared Menu/MenuTrigger pattern and CloseIcon + DoneIcon +
 * UserIcon/GroupIcon aus @blocksuite/icons/rc. Keine externe Search-Service
 * Dependency — client-seitig gefiltert, da die Listen (<=200 Member, <=100
 * Groups) gut in den Speicher passen und bereits einmal vom Panel geladen
 * werden.
 */
import { Menu } from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  DoneIcon,
  GroupIcon,
  SearchIcon,
  UserIcon,
} from '@blocksuite/icons/rc';
import { useMemo, useState } from 'react';

import * as styles from './audit-filters.css';

export interface PrincipalSelection {
  type: 'user' | 'group';
  id: string;
}

export interface PrincipalMemberOption {
  id: string;
  email: string | null;
  name: string | null;
}

export interface PrincipalGroupOption {
  id: string;
  name: string;
  description?: string | null;
}

interface PrincipalFilterSelectProps {
  members: PrincipalMemberOption[];
  groups: PrincipalGroupOption[];
  value: PrincipalSelection | undefined;
  onChange: (value: PrincipalSelection | undefined) => void;
  triggerClassName?: string;
  testId?: string;
}

function memberLabel(m: PrincipalMemberOption): string {
  return m.name || m.email || m.id;
}

export const PrincipalFilterSelect = ({
  members,
  groups,
  value,
  onChange,
  triggerClassName,
  testId,
}: PrincipalFilterSelectProps) => {
  const t = useI18n();
  const [query, setQuery] = useState('');

  const filteredMembers = useMemo(() => {
    if (!query.trim()) return members;
    const q = query.trim().toLowerCase();
    return members.filter(m => {
      const label = memberLabel(m).toLowerCase();
      const email = (m.email ?? '').toLowerCase();
      return label.includes(q) || email.includes(q);
    });
  }, [members, query]);

  const filteredGroups = useMemo(() => {
    if (!query.trim()) return groups;
    const q = query.trim().toLowerCase();
    return groups.filter(g => g.name.toLowerCase().includes(q));
  }, [groups, query]);

  const selectedLabel = useMemo(() => {
    if (!value) {
      return t[
        'com.streamlain.settings.workspace.audit-trail.filter.all-principals'
      ]();
    }
    if (value.type === 'user') {
      const m = members.find(x => x.id === value.id);
      return m ? memberLabel(m) : value.id;
    }
    const g = groups.find(x => x.id === value.id);
    return g
      ? `${t['com.streamlain.settings.workspace.audit-trail.filter.group-prefix']()} ${g.name}`
      : value.id;
  }, [value, members, groups, t]);

  const items = (
    <div className={styles.principalMenu} data-testid="audit-principal-menu">
      <div className={styles.principalSearchWrapper}>
        <SearchIcon
          className={styles.principalSearchIcon}
          width={14}
          height={14}
        />
        <input
          type="text"
          className={styles.principalSearchInput}
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={t[
            'com.streamlain.settings.workspace.audit-trail.filter.search-placeholder'
          ]()}
          autoFocus
          data-testid="audit-principal-search"
        />
      </div>

      <div className={styles.principalScroll}>
        {/* "Alle Nutzer" default option */}
        <div
          className={`${styles.principalItem} ${!value ? styles.principalItemSelected : ''}`}
          onClick={() => {
            onChange(undefined);
            setQuery('');
          }}
          role="option"
          aria-selected={!value}
          data-testid="audit-principal-all"
        >
          <UserIcon
            className={styles.principalItemIcon}
            width={16}
            height={16}
          />
          <span className={styles.principalItemLabel}>
            {t[
              'com.streamlain.settings.workspace.audit-trail.filter.all-principals'
            ]()}
          </span>
          {!value && (
            <DoneIcon
              className={styles.principalItemCheck}
              width={14}
              height={14}
            />
          )}
        </div>

        {/* Groups section */}
        {filteredGroups.length > 0 && (
          <>
            <div className={styles.principalSectionLabel}>
              {t[
                'com.streamlain.settings.workspace.audit-trail.filter.groups-section'
              ]()}
            </div>
            {filteredGroups.map(g => {
              const isSelected = value?.type === 'group' && value.id === g.id;
              return (
                <div
                  key={g.id}
                  className={`${styles.principalItem} ${isSelected ? styles.principalItemSelected : ''}`}
                  onClick={() => {
                    onChange({ type: 'group', id: g.id });
                    setQuery('');
                  }}
                  role="option"
                  aria-selected={isSelected}
                  data-testid={`audit-principal-group-${g.id}`}
                >
                  <GroupIcon
                    className={styles.principalItemIcon}
                    width={16}
                    height={16}
                  />
                  <span className={styles.principalItemLabel}>{g.name}</span>
                  {isSelected && (
                    <DoneIcon
                      className={styles.principalItemCheck}
                      width={14}
                      height={14}
                    />
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* Users section */}
        {filteredMembers.length > 0 && (
          <>
            <div className={styles.principalSectionLabel}>
              {t[
                'com.streamlain.settings.workspace.audit-trail.filter.users-section'
              ]()}
            </div>
            {filteredMembers.map(m => {
              const isSelected = value?.type === 'user' && value.id === m.id;
              return (
                <div
                  key={m.id}
                  className={`${styles.principalItem} ${isSelected ? styles.principalItemSelected : ''}`}
                  onClick={() => {
                    onChange({ type: 'user', id: m.id });
                    setQuery('');
                  }}
                  role="option"
                  aria-selected={isSelected}
                  data-testid={`audit-principal-user-${m.id}`}
                >
                  <UserIcon
                    className={styles.principalItemIcon}
                    width={16}
                    height={16}
                  />
                  <span className={styles.principalItemLabel}>
                    {memberLabel(m)}
                  </span>
                  {isSelected && (
                    <DoneIcon
                      className={styles.principalItemCheck}
                      width={14}
                      height={14}
                    />
                  )}
                </div>
              );
            })}
          </>
        )}

        {filteredMembers.length === 0 && filteredGroups.length === 0 && (
          <div className={styles.principalEmpty}>
            {t[
              'com.streamlain.settings.workspace.audit-trail.filter.no-principals'
            ]()}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Menu items={items} contentOptions={{ align: 'start' }}>
      <button
        type="button"
        className={triggerClassName}
        data-testid={testId ?? 'audit-filter-principal'}
      >
        {selectedLabel}
      </button>
    </Menu>
  );
};
